"use client";

import { useState, useCallback, useRef, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import {
  UploadCloud,
  Trash2,
  Loader2,
  Save,
  Plus,
  Pencil,
  ImageIcon,
  Monitor,
  Smartphone,
} from "lucide-react";
import { useImageUpload } from "@/lib/hooks";
import {
  useWatermarkTemplates,
  useCreateWatermarkTemplate,
  useUpdateWatermarkTemplate,
  useDeleteWatermarkTemplate,
} from "@/lib/hooks/useWatermarkTemplate";
import { toast } from "@/components/ui/use-toast";
import type { WatermarkFormatSettings, WatermarkTemplate } from "@/api/template.service";
import type { WatermarkSettings } from "./video-form-utils";
import { getContrastColor, getContainerAlignment, resizeImageFile, BRANDING_PLACEHOLDER } from "./video-form-utils";
import { googleFonts } from "@/lib/fonts";
import { WatermarkPreview } from "./WatermarkPreview";
import { LogoControls } from "./watermark-step/LogoControls";
import { TitleControls } from "./watermark-step/TitleControls";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

type VideoFormat = "16:9" | "9:16";

const DEFAULT_SETTINGS: WatermarkSettings = {
  logoEnabled: false,
  introEnabled: true,
  opacity: 100,
  alignment: "bottom-right",
  padding: 0,
  size: 10,
  introTitle: "<h2>EDIT TITLE HERE</h2><p>Edit body here</p>",
  introStyle: "cinematic",
  introAnimation: "fade",
  introBgColor: "#000000",
  introBgOpacity: 75,
  introTextColor: "#FFFFFF",
  introFont: "",
  introAlignment: "center",
  contactCard: "",
};

// ─── Removed Legacy FormatEditor and PreviewCard ───────────────────────

// ─── Template Card (List View) ────────────────────────────────────────────

interface TemplateCardProps {
  template: WatermarkTemplate;
  onEdit: (template: WatermarkTemplate) => void;
  onDelete: (id: string) => void;
  isDeleting: boolean;
  isMobile?: boolean;
}

function TemplateCard({ template, onEdit, onDelete, isDeleting, isMobile = false }: TemplateCardProps) {
  const { t } = useTranslation();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const fileName = (() => {
    const urlParts = template.watermarkUrl.split("/");
    return urlParts[urlParts.length - 1]?.split("?")[0] ?? "";
  })();

  return (
    <>
      <div className="group relative flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 p-4 sm:p-5 2k:p-8 rounded-2xl border border-black/5 bg-white hover:border-black/20 hover:shadow-lg transition-all duration-300">
        <div className="flex items-center gap-4 sm:gap-6 flex-1 min-w-0">
          {/* Thumbnail */}
          <div className="relative shrink-0 w-16 h-12 sm:w-24 sm:h-16 2k:w-36 2k:h-24 rounded-xl overflow-hidden bg-gray-50 border border-black/5 flex items-center justify-center p-2">
            <img
              src={template.watermarkUrl || BRANDING_PLACEHOLDER}
              alt={template.name}
              className="max-w-full max-h-full object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).src = BRANDING_PLACEHOLDER;
              }}
            />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <p className="font-bold text-base sm:text-lg 2k:text-3xl truncate text-foreground" title={template.name}>
              {template.name}
            </p>
            <div className="flex flex-col gap-0.5 mt-0.5 sm:mt-1">
              <p className="text-[10px] sm:text-xs text-muted-foreground/60 font-medium truncate 2k:text-lg" title={fileName}>
                {fileName}
              </p>
              <p className="text-[10px] sm:text-xs text-muted-foreground font-medium 2k:text-lg">
                {t("account.templates.watermarkTemplate.savedBadge", {
                  date: new Date(template.updatedAt).toLocaleDateString(),
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        {!isMobile && (
          <div className="flex items-center gap-2 shrink-0 sm:ml-auto pt-3 sm:pt-0 border-t sm:border-0 border-black/5">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onEdit(template)}
              className="flex-1 sm:flex-initial rounded-xl 2k:rounded-2xl h-10 2k:h-16 px-4 2k:px-6 text-xs sm:text-sm 2k:text-xl font-bold gap-2 border-black/10 hover:border-black/30 hover:bg-black/5 transition-all"
            >
              <Pencil className="size-4 2k:size-6" />
              {t("account.templates.watermarkTemplate.editButton")}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              disabled={isDeleting}
              onClick={() => setConfirmOpen(true)}
              className="h-10 w-10 sm:h-10 sm:w-10 2k:size-16 rounded-xl text-destructive hover:text-destructive hover:bg-destructive/5 transition-colors"
            >
              {isDeleting ? <Loader2 className="size-5 animate-spin" /> : <Trash2 className="size-4 sm:size-5 2k:size-7" />}
            </Button>
          </div>
        )}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold">{t("account.templates.watermarkTemplate.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription className="text-base">
              {t("account.templates.watermarkTemplate.deleteDesc", { name: template.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90 rounded-xl"
              onClick={() => { onDelete(template.id); setConfirmOpen(false); }}
            >
              {t("account.templates.watermarkTemplate.deleteConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── Editor Modal Component ───────────────────────────────────────────────

interface EditorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingTemplate: WatermarkTemplate | null;
}

function EditorModal({ open, onOpenChange, editingTemplate }: EditorModalProps) {
  const { t } = useTranslation();
  const createMutation = useCreateWatermarkTemplate();
  const updateMutation = useUpdateWatermarkTemplate();
  const { uploadWatermark, isUploading } = useImageUpload();

  const initialFileName: string | null = editingTemplate?.watermarkUrl
    ? (() => {
        const parts = editingTemplate.watermarkUrl.split("/");
        return parts[parts.length - 1]?.split("?")[0] ?? null;
      })()
    : null;

  // Handle both camelCase (settingsLandscape) and snake_case (settings_landscape) —
  // Encore may serialize field names differently depending on the transport layer.
  const rawLandscape: any =
    editingTemplate?.settingsLandscape ?? (editingTemplate as any)?.settings_landscape;
  const rawPortrait: any =
    editingTemplate?.settingsPortrait ?? (editingTemplate as any)?.settings_portrait;

  const initialLandscape: WatermarkSettings = rawLandscape
    ? {
        logoEnabled: rawLandscape.logoEnabled ?? false,
        introEnabled: rawLandscape.introEnabled ?? true,
        opacity: rawLandscape.opacity ?? DEFAULT_SETTINGS.opacity,
        alignment: rawLandscape.alignment ?? DEFAULT_SETTINGS.alignment,
        padding: rawLandscape.padding ?? DEFAULT_SETTINGS.padding,
        size: rawLandscape.size ?? DEFAULT_SETTINGS.size,
        introTitle: rawLandscape.introTitle ?? "",
        introStyle: (rawLandscape.introStyle as any) ?? "cinematic",
        introAnimation: rawLandscape.introAnimation ?? "fade",
        introBgColor: rawLandscape.introBgColor ?? "#000000",
        introBgOpacity: rawLandscape.introBgOpacity ?? 75,
        introTextColor: rawLandscape.introTextColor ?? "#FFFFFF",
        introFont: rawLandscape.introFont ?? "",
        introAlignment: rawLandscape.introAlignment ?? "center",
        contactCard: rawLandscape.contactCard ?? "",
      }
    : { ...DEFAULT_SETTINGS };

  const initialPortrait: WatermarkSettings = rawPortrait
    ? {
        logoEnabled: rawPortrait.logoEnabled ?? false,
        introEnabled: rawPortrait.introEnabled ?? true,
        opacity: rawPortrait.opacity ?? DEFAULT_SETTINGS.opacity,
        alignment: rawPortrait.alignment ?? DEFAULT_SETTINGS.alignment,
        padding: rawPortrait.padding ?? DEFAULT_SETTINGS.padding,
        size: rawPortrait.size ?? DEFAULT_SETTINGS.size,
        introTitle: rawPortrait.introTitle ?? "",
        introStyle: (rawPortrait.introStyle as any) ?? "cinematic",
        introAnimation: rawPortrait.introAnimation ?? "fade",
        introBgColor: rawPortrait.introBgColor ?? "#000000",
        introBgOpacity: rawPortrait.introBgOpacity ?? 75,
        introTextColor: rawPortrait.introTextColor ?? "#FFFFFF",
        introFont: rawPortrait.introFont ?? "",
        introAlignment: rawPortrait.introAlignment ?? "center",
        contactCard: rawPortrait.contactCard ?? "",
      }
    : { ...DEFAULT_SETTINGS };

  const [templateName, setTemplateName] = useState(editingTemplate?.name ?? "");
  const [watermarkUrl, setWatermarkUrl] = useState<string | null>(editingTemplate?.watermarkUrl ?? null);
  const [watermarkPreview, setWatermarkPreview] = useState<string | null>(editingTemplate?.watermarkUrl ?? null);
  const [watermarkFileName, setWatermarkFileName] = useState<string | null>(initialFileName);
  const [activeFormat, setActiveFormat] = useState<VideoFormat>("16:9");
  const [settingsByFormat, setSettingsByFormat] = useState<Record<VideoFormat, WatermarkSettings>>({
    "16:9": initialLandscape,
    "9:16": initialPortrait,
  });

  const [isPlaying, setIsPlaying] = useState(false);
  const [previewTrigger, setPreviewTrigger] = useState(0);
  const [stopTrigger, setStopTrigger] = useState(0);
  const [activeBrandingTab, setActiveBrandingTab] = useState<"intro" | "logo">("intro");
  const [isCapturing, setIsCapturing] = useState(false);
  const [capturePhase, setCapturePhase] = useState<"processing" | null>(null);
  const [captureProgress, setCaptureProgress] = useState(0);

  const handleCapturePhaseChange = useCallback((phase: "processing" | null, progress: number) => {
    setCapturePhase(phase);
    setCaptureProgress(progress);
  }, []);
  
  const captureRefs = {
    "16:9": useRef<{ captureIntroClip: () => Promise<string[]> } | null>(null),
    "9:16": useRef<{ captureIntroClip: () => Promise<string[]> } | null>(null),
  };

  const brandingTabs = [
    { id: 'intro' as const, label: t("createVideo.brandingTabs.intro") || "Opening Title" },
    { id: 'logo' as const, label: t("createVideo.brandingTabs.logo") || "Logo" },
  ];

  const introStylesList = [
    { id: 'cinematic', label: t("createVideo.introStyles.cinematic") || "Luxury" },
    { id: 'architect', label: t("createVideo.introStyles.architect") || "Structural" },
    { id: 'editorial', label: t("createVideo.introStyles.editorial") || "Elegant" },
    { id: 'geometric', label: t("createVideo.introStyles.geometric") || "Bold" },
    { id: 'typewriter', label: t("createVideo.introStyles.typewriter") || "Journal" },
  ] as const;

  const presetColors = [
    { name: "Black", value: "#000000" },
    { name: "White", value: "#FFFFFF" },
    { name: "Beige", value: "#F5F5DC" },
    { name: "Slate", value: "#4B5563" },
    { name: "Navy", value: "#1E3A8A" },
    { name: "Amber", value: "#B45309" },
    { name: "Crimson", value: "#991B1B" },
  ];

  const handleWatermarkUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setWatermarkFileName(file.name);
    const resized = await resizeImageFile(file, 480);
    setWatermarkPreview(URL.createObjectURL(resized));
    const uploadedUrl = await uploadWatermark(resized);
    if (uploadedUrl) setWatermarkUrl(uploadedUrl);
  };
  const handleSettingsChange = useCallback(
    (format: VideoFormat, updated: Partial<WatermarkSettings>) => {
      setSettingsByFormat((prev) => ({ ...prev, [format]: { ...prev[format], ...updated } }));
    },
    []
  );

  const handleSave = async () => {
    if (!templateName.trim()) return;

    const finalSettings = { ...settingsByFormat };

    // Process intro video for each format if title is present
    for (const format of ["16:9", "9:16"] as const) {
      const settings = finalSettings[format];
      if (settings.introEnabled && settings.introTitle && settings.introTitle.trim().length > 0) {
        
        // Check if capture is needed
        let captureNeeded = true;
        const rawSettings = format === "16:9" ? rawLandscape : rawPortrait;

        if (rawSettings) {
          const relevantOrig = {
            introEnabled: rawSettings.introEnabled ?? true,
            introTitle: rawSettings.introTitle ?? "",
            introStyle: rawSettings.introStyle ?? "cinematic",
            introAnimation: rawSettings.introAnimation ?? "fade",
            introBgColor: rawSettings.introBgColor ?? "#000000",
            introBgOpacity: rawSettings.introBgOpacity ?? 75,
            introTextColor: rawSettings.introTextColor ?? "#FFFFFF",
            introFont: rawSettings.introFont ?? "",
            introAlignment: rawSettings.introAlignment ?? "center",
            contactCard: rawSettings.contactCard ?? "",
          };

          const relevantCurrent = {
            introEnabled: settings.introEnabled,
            introTitle: settings.introTitle,
            introStyle: settings.introStyle,
            introAnimation: settings.introAnimation,
            introBgColor: settings.introBgColor,
            introBgOpacity: settings.introBgOpacity,
            introTextColor: settings.introTextColor,
            introFont: settings.introFont,
            introAlignment: settings.introAlignment,
            contactCard: settings.contactCard,
          };

          // Skip capture if settings unchanged AND frames already exist
          if (JSON.stringify(relevantOrig) === JSON.stringify(relevantCurrent) &&
              rawSettings.introFrameUrls && rawSettings.introFrameUrls.length > 0) {
            captureNeeded = false;
            finalSettings[format] = {
              ...settings,
              introFrameUrls: rawSettings.introFrameUrls,
            };
          }
        }

        if (captureNeeded) {
          const captureRef = captureRefs[format].current;
          if (captureRef) {
            setIsCapturing(true);
            try {
              const urls = await captureRef.captureIntroClip();
              if (urls && urls.length > 0) {
                finalSettings[format] = {
                  ...settings,
                  introFrameUrls: urls,
                };
              }
            } catch (err) {
              console.error(`[handleSave] failed to capture ${format}:`, err);
              toast({
                variant: "destructive",
                title: t("createVideo.captureError") || "Intro Capture Failed",
                description: t("createVideo.captureErrorDesc") || "Could not generate the branding animation.",
              });
            } finally {
              setIsCapturing(false);
            }
          }
        }
      }
    }

    const payload = {
      name: templateName.trim(),
      watermarkUrl: watermarkUrl || "",
      settingsLandscape: finalSettings["16:9"] as any,
      settingsPortrait: finalSettings["9:16"] as any,
    };

    if (editingTemplate) {
      await updateMutation.mutateAsync({ id: editingTemplate.id, ...payload });
      toast({
        title: t("account.templates.watermarkTemplate.saveSuccess"),
        description: t("account.templates.watermarkTemplate.saveSuccessDesc"),
      });
    } else {
      await createMutation.mutateAsync(payload);
      toast({
        title: t("account.templates.watermarkTemplate.saveSuccess"),
        description: t("account.templates.watermarkTemplate.saveSuccessDesc"),
      });
    }
    onOpenChange(false);
  };

  const isBusy = createMutation.isPending || updateMutation.isPending || isUploading || isCapturing;
  const editorId = editingTemplate?.id ?? "new";

  return (
    <Dialog open={open} onOpenChange={(val) => !isBusy && onOpenChange(val)}>
      <DialogContent className="max-w-6xl 2k:max-w-7xl overflow-y-auto max-h-[95vh] rounded-[32px] border-none shadow-2xl">
        <DialogHeader className="pb-4 border-b border-black/5">
          <DialogTitle className="text-2xl 2k:text-4xl font-black text-foreground">
            {editingTemplate
              ? t("account.templates.watermarkTemplate.editTitle")
              : t("account.templates.watermarkTemplate.newTitle")}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-10 py-6">
          {/* Section 1: Identity */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="space-y-2 flex flex-col">
              <Label htmlFor={`${editorId}-tmpl-name`} className="font-bold text-base 2k:text-2xl">
                {t("account.templates.watermarkTemplate.nameLabel")}
              </Label>
              <Input
                id={`${editorId}-tmpl-name`}
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                className="rounded-xl border-black/10 focus:border-black h-12 2k:h-18"
                placeholder={t("account.templates.watermarkTemplate.namePlaceholder")}
                disabled={isBusy}
              />
            </div>

            <div className="md:col-span-2 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="font-bold text-base 2k:text-2xl">{t("createVideo.watermarkLabel")}</Label>
                {watermarkPreview && (
                  <Button type="button" variant="ghost" size="sm" disabled={isBusy}
                    onClick={() => { setWatermarkUrl(null); setWatermarkPreview(null); setWatermarkFileName(null); }}
                    className="text-destructive hover:bg-destructive/5 font-bold gap-2"
                  >
                    <Trash2 className="size-4" />
                    {t("createVideo.removeWatermark")}
                  </Button>
                )}
              </div>
              
              {!watermarkPreview ? (
                <label htmlFor={`${editorId}-watermark-input`}
                  className="flex flex-col items-center justify-center w-full h-32 2k:h-48 border-2 border-dashed border-black/10 rounded-2xl cursor-pointer bg-gray-50 hover:bg-black/2 hover:border-black/30 transition-all">
                  <div className="flex items-center gap-4 px-6">
                    <UploadCloud className="size-8 text-gray-400" />
                    <div className="text-left">
                      <p className="text-base font-bold text-foreground">{t("createVideo.clickToUpload")}</p>
                      <p className="text-xs text-muted-foreground mt-1">{t("createVideo.pngOrJpg")}</p>
                    </div>
                  </div>
                  <input id={`${editorId}-watermark-input`} type="file" className="hidden"
                    onChange={handleWatermarkUpload} accept="image/png, image/jpeg" disabled={isBusy} />
                </label>
              ) : (
                <div className="flex items-center gap-4 p-4 bg-black/2 rounded-2xl border border-black/5">
                  <div className="w-16 h-12 bg-white rounded-lg border border-black/10 flex items-center justify-center p-1 overflow-hidden">
                    <img src={watermarkPreview} alt="Logo" className="max-w-full max-h-full object-contain" />
                  </div>
                  <div className="flex-1 min-w-0">
                    {isUploading ? (
                      <>
                        <p className="text-sm font-bold text-foreground truncate">{t("account.templates.watermarkTemplate.uploading")}</p>
                        <Loader2 className="size-3 animate-spin text-muted-foreground mt-1" />
                      </>
                    ) : (
                      <p className="text-sm font-bold text-foreground truncate" title={watermarkFileName || ""}>{watermarkFileName || t("createVideo.watermarkLabel")}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Section 2: Format Settings with Custom Tabs */}
            <div className="space-y-6 pt-4 border-t border-black/5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="space-y-1">
                  <h4 className="font-bold text-lg 2k:text-3xl">{t("account.templates.watermarkTemplate.title")} Settings</h4>
                  <p className="text-sm text-muted-foreground">{t("account.templates.watermarkTemplate.description")}</p>
                </div>
                
                {/* Format Switcher */}
                <div className="flex bg-black/5 p-1.5 rounded-2xl w-fit self-start">
                  <button
                    type="button"
                    onClick={() => setActiveFormat("16:9")}
                    className={cn(
                      "px-8 py-3 rounded-xl text-sm font-bold transition-all flex items-center gap-2",
                      activeFormat === "16:9" ? "bg-white shadow-sm text-black" : "text-muted-foreground hover:text-black"
                    )}
                  >
                    <Monitor className="size-4" />
                    {t("createVideo.horizontal")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveFormat("9:16")}
                    className={cn(
                      "px-8 py-3 rounded-xl text-sm font-bold transition-all flex items-center gap-2",
                      activeFormat === "9:16" ? "bg-white shadow-sm text-black" : "text-muted-foreground hover:text-black"
                    )}
                  >
                    <Smartphone className="size-4" />
                    {t("createVideo.vertical")}
                  </button>
                </div>
              </div>

              {/* Active Format Editor */}
              <div className="p-8 bg-white/50 rounded-[32px] border border-black/5 space-y-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
                  <div className="lg:col-span-2 space-y-6">
                  <WatermarkPreview
                    aspectRatio={activeFormat}
                    watermarkPreview={watermarkPreview || BRANDING_PLACEHOLDER}
                    watermarkAlignment={settingsByFormat[activeFormat].alignment}
                    watermarkPadding={settingsByFormat[activeFormat].padding}
                    watermarkOpacity={settingsByFormat[activeFormat].opacity}
                    watermarkSize={settingsByFormat[activeFormat].size}
                    logoEnabled={settingsByFormat[activeFormat].logoEnabled}
                    introEnabled={settingsByFormat[activeFormat].introEnabled}
                    introAlignment={settingsByFormat[activeFormat].introAlignment}
                    introTitle={settingsByFormat[activeFormat].introTitle}
                    introStyle={settingsByFormat[activeFormat].introStyle}
                    introAnimation={settingsByFormat[activeFormat].introAnimation}
                    introBgColor={settingsByFormat[activeFormat].introBgColor}
                    introBgOpacity={settingsByFormat[activeFormat].introBgOpacity}
                    introTextColor={settingsByFormat[activeFormat].introTextColor || "#FFFFFF"}
                    introFont={settingsByFormat[activeFormat].introFont}
                    playTrigger={previewTrigger}
                    stopTrigger={stopTrigger}
                    onPlayStateChange={setIsPlaying}
                    onIntroTitleChange={(title: string) => {
                      setSettingsByFormat((prev) => ({
                        ...prev,
                        [activeFormat]: { ...prev[activeFormat], introTitle: title },
                      }));
                    }}
                    contactCard={settingsByFormat[activeFormat].contactCard || ""}
                    onContactCardChange={(html) =>
                      setSettingsByFormat((prev) => ({
                        ...prev,
                        [activeFormat]: { ...prev[activeFormat], contactCard: html },
                      }))
                    }
                    getContainerAlignment={getContainerAlignment}
                    activeTab={activeBrandingTab}
                  />
                </div>

                <div className="lg:col-span-1 space-y-8 animate-in fade-in slide-in-from-right-4 duration-500">
                  <div className="flex bg-black/[0.03] p-1.5 rounded-2xl w-full border border-black/5 shadow-inner backdrop-blur-sm">
                    {brandingTabs.map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        disabled={isPlaying}
                        onClick={() => {
                          setActiveBrandingTab(tab.id);
                          setStopTrigger(Date.now());
                        }}
                        className={cn(
                          "flex-1 py-3 px-2 flex items-center justify-center text-[10px] sm:text-xs font-bold rounded-xl transition-all duration-300 relative",
                          activeBrandingTab === tab.id
                            ? "bg-white text-black shadow-[0_2px_10px_rgba(0,0,0,0.1)] scale-[1.02]"
                            : "text-muted-foreground hover:text-black hover:bg-black/[0.02]",
                          isPlaying && "opacity-50 cursor-not-allowed grayscale"
                        )}
                      >
                        <span className="truncate">{tab.label}</span>
                      </button>
                    ))}
                  </div>

                  {activeBrandingTab === "logo" && (
                    <LogoControls 
                      format={activeFormat}
                      watermarkSettingsByFormat={settingsByFormat}
                      setWatermarkSettingsByFormat={setSettingsByFormat}
                      isPlaying={isPlaying}
                      watermarkPreview={watermarkPreview}
                      handleWatermarkUpload={handleWatermarkUpload}
                      activeTemplate={editingTemplate}
                      getContainerAlignment={getContainerAlignment}
                    />
                  )}

                  {activeBrandingTab === "intro" && (
                    <TitleControls 
                      format={activeFormat}
                      watermarkSettingsByFormat={settingsByFormat}
                      setWatermarkSettingsByFormat={setSettingsByFormat}
                      isPlaying={isPlaying}
                      introStylesList={introStylesList}
                      presetColors={presetColors}
                      getContrastColor={getContrastColor}
                      setStopTrigger={setStopTrigger}
                      setPreviewTrigger={setPreviewTrigger}
                      getContainerAlignment={getContainerAlignment}
                      googleFonts={googleFonts}
                    />
                  )}
                </div>
              </div>
              </div>
            </div>
        </div>

        {/* Hidden Background Capture Previews */}
        <div className="fixed left-[-9999px] top-0 pointer-events-none" aria-hidden="true">
          {(["16:9", "9:16"] as const).map(format => (
            <WatermarkPreview
              key={`capture-${format}`}
              captureIntroClipRef={captureRefs[format]}
              onCapturePhaseChange={handleCapturePhaseChange}
              aspectRatio={format}
              watermarkPreview={watermarkUrl || BRANDING_PLACEHOLDER}
              watermarkAlignment={settingsByFormat[format].alignment}
              watermarkPadding={settingsByFormat[format].padding}
              watermarkOpacity={settingsByFormat[format].opacity}
              watermarkSize={settingsByFormat[format].size}
              logoEnabled={settingsByFormat[format].logoEnabled}
              introEnabled={settingsByFormat[format].introEnabled}
              introAlignment={settingsByFormat[format].introAlignment}
              introTitle={settingsByFormat[format].introTitle}
              introStyle={settingsByFormat[format].introStyle}
              introAnimation={settingsByFormat[format].introAnimation}
              introBgColor={settingsByFormat[format].introBgColor}
              introBgOpacity={settingsByFormat[format].introBgOpacity}
              introTextColor={settingsByFormat[format].introTextColor || "#FFFFFF"}
              introFont={settingsByFormat[format].introFont}
              contactCard={settingsByFormat[format].contactCard || ""}
              getContainerAlignment={getContainerAlignment}
            />
          ))}
        </div>

        <DialogFooter className="gap-3 pt-8 border-t border-black/5 sm:justify-end">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isBusy}
            className="rounded-xl h-12 2k:h-18 px-8 font-bold hover:bg-black/5">
            {t("common.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={isBusy || !templateName.trim()}
            className="bg-black hover:bg-black/90 text-white rounded-xl h-12 2k:h-18 px-10 font-bold shadow-lg hover:shadow-xl transition-all gap-2">
            {isBusy ? <Loader2 className="size-5 animate-spin" /> : <Save className="size-5" />}
            {isBusy
              ? t("account.templates.watermarkTemplate.saving")
              : editingTemplate 
                ? (t("createVideo.saveChanges") || "Save Changes")
                : t("account.templates.watermarkTemplate.saveButton")}
          </Button>
        </DialogFooter>
      </DialogContent>
      {/* NOTE: Capturing Progress Dialog commented out since it's simply capture and upload. Will be used/re-enabled later if needed. */}
      {/* 
      <AlertDialog open={isCapturing}>
        <AlertDialogContent className="sm:max-w-md flex flex-col items-center justify-center p-12 space-y-6">
          <div className="relative flex items-center justify-center w-16 h-16">
            <div className="absolute w-full h-full border-4 border-black/10 rounded-full" />
            <div className="absolute w-full h-full border-4 border-black border-t-transparent rounded-full animate-spin" />
          </div>
          <AlertDialogTitle className="text-2xl font-medium text-center">
            {`${t("createVideo.preparingBranding") || "Preparing branding"} (${captureProgress}%)`}
          </AlertDialogTitle>
          <div className="w-full bg-black/10 rounded-full h-2 overflow-hidden">
            <div className="bg-black h-full transition-all duration-300" style={{ width: `${captureProgress}%` }} />
          </div>
          <AlertDialogDescription className="text-base text-center">
            {t("createVideo.pleaseWait") || "Please wait while we process the branding animation. Do not close this page."}
          </AlertDialogDescription>
        </AlertDialogContent>
      </AlertDialog>
      */}
    </Dialog>
  );
}

// ─── Root Component ───────────────────────────────────────────────────────

export function WatermarkTemplateEditor() {
  const { t } = useTranslation();
  const { data, isLoading } = useWatermarkTemplates();
  const deleteMutation = useDeleteWatermarkTemplate();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<WatermarkTemplate | null>(null);
  // A stable key that changes every time we open a specific template,
  // forcing EditorModal to fully remount with fresh state.
  const [editorKey, setEditorKey] = useState<string>("new");

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );

  useEffect(() => {
    const check = () => setIsMobile(window.matchMedia('(max-width: 768px)').matches);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const templates = data?.templates ?? [];

  const handleEdit = (tmpl: WatermarkTemplate) => {
    setEditingTemplate(tmpl);
    setEditorKey(tmpl.id);  // force remount with template data
    setIsDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingTemplate(null);
    setEditorKey(`new-${Date.now()}`);  // force remount with empty state
    setIsDialogOpen(true);
  };

  const handleDialogOpenChange = (open: boolean) => {
    setIsDialogOpen(open);
    // Don't reset editingTemplate here — that would cause a key change
    // during the close animation and flash empty state.
    // The editorKey guarantees a fresh mount on the NEXT open.
  };

  const handleDelete = async (id: string) => {
    await deleteMutation.mutateAsync(id);
    toast({
      title: t("account.templates.watermarkTemplate.clearSuccess"),
      description: t("account.templates.watermarkTemplate.clearSuccessDesc"),
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-10 h-10 animate-spin text-black/20" />
      </div>
    );
  }

  const MobileNoteBanner = () => (
    <div className="p-4 mb-6 bg-blue-50 border border-blue-200 rounded-2xl flex gap-3 items-center text-blue-800 text-xs 2k:text-lg 4k:text-2xl font-medium">
      <span className="shrink-0 font-bold bg-blue-100 rounded-full px-2.5 py-0.5">Note</span>
      <p>{t("createVideo.mobileBrandingNote") || "Branding templates can only be added or edited using the desktop version of the app."}</p>
    </div>
  );

  return (
    <div className="space-y-8 2k:space-y-14">
      {isMobile && <MobileNoteBanner />}

      {/* Description + Add button */}
      {templates.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 pb-4 border-b border-black/5">
          <div className="space-y-1">
            <h4 className="font-bold text-xl 2k:text-3xl text-foreground">{t("account.templates.watermarkTemplate.title")}</h4>
            <p className="text-sm 2k:text-xl text-muted-foreground max-w-xl">
              {t("account.templates.watermarkTemplate.description")}
            </p>
          </div>
          {!isMobile && (
            <Button
              onClick={handleAdd}
              className="shrink-0 bg-black hover:bg-black/90 text-white rounded-2xl h-12 2k:h-20 px-6 2k:px-10 text-base 2k:text-2xl font-black gap-2 shadow-lg hover:shadow-xl transition-all"
            >
              <Plus className="size-5 2k:size-8" />
              {t("account.templates.watermarkTemplate.addButton")}
            </Button>
          )}
        </div>
      )}

      {/* Template list */}
      {templates.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 2k:py-32 text-center bg-white border border-black/5 rounded-3xl shadow-sm">
          <div className="w-16 h-16 2k:w-24 2k:h-24 bg-gray-50 rounded-full flex items-center justify-center mb-6 border border-black/5">
            <ImageIcon className="size-8 2k:size-12 text-black/20" />
          </div>
          <h2 className="text-2xl 2k:text-4xl font-bold text-foreground mb-2">
            {t("account.templates.watermarkTemplate.emptyState")}
          </h2>
          <p className="text-sm 2k:text-xl text-muted-foreground max-w-sm mb-8">
            {t("account.templates.watermarkTemplate.description")}
          </p>
          {!isMobile && (
            <Button
              onClick={handleAdd}
              className="bg-black hover:bg-black/90 text-white rounded-xl h-11 2k:h-16 px-6 2k:px-10 text-sm 2k:text-xl font-bold gap-2 shadow-md hover:shadow-lg transition-all"
            >
              <Plus className="size-4 2k:size-6" />
              {t("account.templates.watermarkTemplate.addButton")}
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 2k:gap-8">
          {templates.map((tmpl) => (
            <TemplateCard
              key={tmpl.id}
              template={tmpl}
              onEdit={handleEdit}
              onDelete={handleDelete}
              isDeleting={deleteMutation.isPending && deleteMutation.variables === tmpl.id}
              isMobile={isMobile}
            />
          ))}
        </div>
      )}

      {/* Editor Dialog */}
      {isDialogOpen && !isMobile && (
        <EditorModal
          key={editorKey}
          open={isDialogOpen}
          onOpenChange={handleDialogOpenChange}
          editingTemplate={editingTemplate}
        />
      )}
    </div>
  );
}
